import React from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import Branded from '../components/Branded';

const Section = styled.section`
  background: #fff;
  padding: 100px 0;

  @media (max-width: 768px) {
    padding: 64px 0;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 72px;

  @media (max-width: 768px) {
    margin-bottom: 48px;
  }

  .tag {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 12px;
  }

  h2 {
    font-size: 2.2rem;
    font-weight: 700;
    color: #111;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ count }) => count}, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  cursor: default;
  height: 320px;

  @media (max-width: 540px) {
    height: 280px;
  }

  .step-img {
    position: absolute;
    inset: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        to top,
        rgba(0, 0, 0, 0.5) 0%,
        transparent 60%
      );
    }
  }

  &:hover .step-img img {
    transform: scale(1.04);
  }

  /* Pure dark layer behind the text — only this animates on hover.
     The text (.step-body) stays fixed on top. */
  .step-overlay {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 45%;
    background: rgba(0, 0, 0, 0.8);
    transform: scaleY(0);
    transform-origin: bottom;
    transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    pointer-events: none;
    z-index: 1;
  }

  &:hover .step-overlay {
    transform: scaleY(1);
  }

  .step-body {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 28px;
    z-index: 2;
  }

  .step-title {
    font-size: 17px;
    font-weight: 500;
    color: #fff;
    margin-bottom: 8px;
    line-height: 1.3;
    -webkit-text-stroke: 1px #000;
    paint-order: stroke fill;
  }

  .step-desc {
    font-size: 13px;
    color: #fff;
    line-height: 1.7;
    -webkit-text-stroke: 0.5px #000;
    paint-order: stroke fill;

    /* Brand blue is too dark against the photo overlay */
    .brand-space {
      color: #8fc4f7;
    }
  }

  &:hover .step-title,
  &:hover .step-desc {
    color: #fff;
    -webkit-text-stroke: 0;
  }
`;

const NoImg = styled.div`
  width: 100%;
  height: 220px;
  background: #0b5db8;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-size: 3rem;
    font-weight: 200;
    color: rgba(240, 128, 32, 0.5);
  }
`;

const CtaWrap = styled.div`
  text-align: center;
  margin-top: 56px;

  a {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #1378e5;
    padding: 13px 32px;
    border: 1px solid #1378e5;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

    svg {
      width: 14px;
      height: 14px;
      stroke: #1378e5;
      fill: none;
      stroke-width: 1.5;
      stroke-linecap: round;
      stroke-linejoin: round;
      transition: transform 0.3s ease, stroke 0.3s ease;
    }

    &:hover {
      background: #1378e5;
      color: #fff;
      svg {
        stroke: #fff;
        transform: translateX(4px);
      }
    }
  }
`;

export default function Process({ tag, title, steps }) {
  const data = steps || [];

  if (!data.length) return null;

  return (
    <Section>
      <Container>
        <Header>
          {tag && <div className="tag">{tag}</div>}
          <h2>{title || 'Our Process'}</h2>
        </Header>

        <Steps count={data.length}>
          {data.map(({ _key, title: t, description, icon }) => (
            <Step key={_key}>
              {icon?.asset?.url && (
                <div className="step-img">
                  <img
                    src={icon.asset.url + '?w=500&auto=format'}
                    alt={t}
                    loading="lazy"
                  />
                </div>
              )}
              <div className="step-overlay" />
              <div className="step-body">
                <div className="step-title">{t}</div>
                <Branded as="div" className="step-desc" text={description} />
              </div>
            </Step>
          ))}
        </Steps>

        <CtaWrap>
          <Link to="/services">
            Find Out More
            <svg viewBox="0 0 24 24">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </CtaWrap>
      </Container>
    </Section>
  );
}
